import type {
  DynamicDocumentationEligibilityRequest,
  StatifiedDocumentationEligibilityRequest,
} from 'apiConnector/form/documentation';
import { DocumentationV3 } from 'epic/common-form/Documentation';
import { FormError } from 'models/formError';
import type { ProductLayoutTree } from 'models/forms/FormResult';
import { FormBlockMetadataById } from 'models/metadata/block.definition';
import { NextWorkflowError } from 'models/NextWorkflowError';
import { EnrichmentError, QuoteAction } from 'state/common-form/infos/infos.model';
import { FieldMetadataById } from 'state/common-form/metaData/fields/fields.model';
import { ProductGraph } from 'state/product-description/model';
import { StaticProductView } from 'state/product-description/types';

import type { HandledWorkspace } from '../models';

export type CommonFormAction = ReturnType<
  (typeof commonFormActions)[Exclude<keyof typeof commonFormActions, 'isHandled'>]
>;

export const commonFormActions = {
  isHandled(workspace: string): workspace is HandledWorkspace {
    return workspace === 'essential' || workspace === 'dynamic' || workspace === 'static';
  },
  formEnrichmentErrorDiscarded(workspace: HandledWorkspace, tabId: string) {
    return {
      type: 'FORM_ENRICHMENT_ERROR_DISCARDED' as const,
      tabId,
      workspace,
    };
  },
  defaultDynamicFormReceived(
    tabId: string,
    instrumentName: string,
    instrumentContractName: string,
    productGraph: ProductGraph,
    blocksMetadata: FormBlockMetadataById,
    fieldsMetadata: FieldMetadataById,
    version: number,
    negotiationId: string | undefined,
    actions: readonly QuoteAction[],
    quoteQuery: unknown,
    remunerationMode: string,
    validationErrors?: readonly FormError[],
    etag?: string,
    sendWholeFormData?: boolean,
  ) {
    return {
      type: 'DEFAULT_FORM_RECEIVED' as const,
      workspace: 'dynamic' as const,
      tabId,
      instrumentName,
      instrumentContractName,
      productGraph,
      blocksMetadata,
      fieldsMetadata,
      version,
      negotiationId,
      actions,
      quoteQuery,
      remunerationMode,
      validationErrors,
      etag,
      sendWholeFormData,
    };
  },
  defaultStatifiedFormReceived(
    workspace: Exclude<HandledWorkspace, 'dynamic'>,
    tabId: string,
    productView: StaticProductView,
    productDescription: string,
    instrumentName: string,
    blocksMetadata: FormBlockMetadataById,
    fieldsMetadata: FieldMetadataById,
    version: number,
    negotiationId: string | undefined,
    actions: readonly QuoteAction[],
    quoteQuery: unknown,
    remunerationMode: string,
    mustForceAdvancedMode: boolean,
    validationErrors?: readonly FormError[],
    sendWholeFormData?: boolean,
    productLayout?: ProductLayoutTree,
  ) {
    return {
      type: 'DEFAULT_FORM_RECEIVED' as const,
      tabId,
      workspace,
      productView,
      productDescription,
      instrumentName,
      blocksMetadata,
      fieldsMetadata,
      version,
      negotiationId,
      actions,
      quoteQuery,
      remunerationMode,
      mustForceAdvancedMode,
      validationErrors,
      sendWholeFormData,
      productLayout,
    };
  },
  defaultFormFailed(workspace: HandledWorkspace, tabId: string, error: string, traceId: string | undefined) {
    return {
      type: 'DEFAULT_FORM_FAILED' as const,
      tabId,
      error,
      traceId,
      workspace,
    };
  },
  defaultFormTimeouted(workspace: HandledWorkspace, tabId: string) {
    return {
      type: 'DEFAULT_FORM_TIMEOUTED' as const,
      tabId,
      workspace,
    };
  },
  productCodesRequested(workspace: HandledWorkspace, tabId: string) {
    return {
      type: 'PRODUCT_CODES_REQUESTED' as const,
      tabId,
      workspace,
    };
  },
  productCodesRejected(workspace: HandledWorkspace, tabId: string, errors: readonly NextWorkflowError[]) {
    return {
      type: 'PRODUCT_CODES_REJECTED' as const,
      tabId,
      errors,
      workspace,
    };
  },
  formDocumentationFetchRequested(
    workspace: HandledWorkspace,
    tabId: string,
    request: StatifiedDocumentationEligibilityRequest | DynamicDocumentationEligibilityRequest,
  ) {
    return {
      type: 'FORM_DOCUMENTATION_FETCH_REQUESTED' as const,
      tabId,
      workspace,
      negotiationId: request.negotiationId,
      request,
    };
  },
  formDocumentationV3Received(workspace: HandledWorkspace, tabId: string, documentation: DocumentationV3) {
    return {
      type: 'FORM_DOCUMENTATION_V3_RECEIVED' as const,
      tabId,
      documentation,
      workspace,
    };
  },
  formTabRollbacked(workspace: HandledWorkspace, tabId: string, negotiationId: string) {
    return {
      type: 'FORM_TAB_ROLLBACKED' as const,
      tabId,
      negotiationId,
      workspace,
    };
  },
  updateRfqVersion(workspace: HandledWorkspace, tabId: string, newVersion: number) {
    return {
      type: 'UPDATE_RFQ_VERSION' as const,
      newVersion,
      tabId,
      workspace,
    };
  },
  quoteActiveFormTabChanged(workspace: Exclude<HandledWorkspace, 'essential'>, tabId: string, formTabId: string) {
    return {
      type: 'QUOTE_ACTIVE_FORM_TAB_CHANGED' as const,
      tabId,
      formTabId,
      workspace,
    };
  },
  fetchFormEnrichmentFailed(workspace: HandledWorkspace, tabId: string, error: EnrichmentError | Error) {
    return {
      type: 'FORM_ENRICHMENT_FAILED' as const,
      tabId,
      error,
      workspace,
    };
  },
  formReloadFailed(workspace: HandledWorkspace, tabId: string, error?: string) {
    return {
      type: 'FORM_RELOAD_FAILED' as const,
      tabId,
      error,
      workspace,
    };
  },
  statifiedFormEnrichmentReceived(
    workspace: Exclude<HandledWorkspace, 'dynamic'>,
    tabId: string,
    productView: StaticProductView,
    productDescription: string,
    instrumentName: string,
    blocksMetadata: FormBlockMetadataById,
    fieldsMetadata: FieldMetadataById,
    version: number,
    actions: readonly QuoteAction[],
    quoteQuery: unknown,
    remunerationMode: string,
    mustForceAdvancedMode: boolean,
    validationErrors?: readonly FormError[],
    productLayout?: ProductLayoutTree,
  ) {
    return {
      type: 'FORM_ENRICHMENT_RECEIVED' as const,
      workspace,
      tabId,
      productView,
      instrumentName,
      productDescription,
      blocksMetadata,
      fieldsMetadata,
      version,
      actions,
      quoteQuery,
      remunerationMode,
      validationErrors,
      mustForceAdvancedMode,
      productLayout,
    };
  },
  dynamicFormEnrichmentReceived(
    tabId: string,
    instrumentName: string,
    instrumentContractName: string,
    blocksMetadata: FormBlockMetadataById,
    fieldsMetadata: FieldMetadataById,
    productGraph: ProductGraph,
    version: number,
    actions: readonly QuoteAction[],
    quoteQuery: unknown,
    remunerationMode: string,
    validationErrors?: readonly FormError[],
  ) {
    return {
      type: 'FORM_ENRICHMENT_RECEIVED' as const,
      workspace: 'dynamic' as const,
      tabId,
      instrumentName,
      instrumentContractName,
      blocksMetadata,
      fieldsMetadata,
      productGraph,
      version,
      actions,
      quoteQuery,
      remunerationMode,
      validationErrors,
    };
  },
  statifiedFormReloaded(
    workspace: Exclude<HandledWorkspace, 'dynamic'>,
    tabId: string,
    data: {
      productView: StaticProductView;
      negotiationId?: string;
      productDescription: string;
      instrumentName: string;
      blocksMetadata: FormBlockMetadataById;
      fieldsMetadata: FieldMetadataById;
      version: number;
      actions: readonly QuoteAction[];
      quoteQuery: unknown;
      remunerationMode: string;
      mustForceAdvancedMode: boolean;
      validationErrors?: readonly FormError[];
      sendWholeFormData?: boolean;
      productLayout?: ProductLayoutTree;
    },
  ) {
    return {
      type: 'FORM_RELOADED' as const,
      tabId,
      ...data,
      workspace,
    };
  },
  dynamicFormReloaded(
    tabId: string,
    data: {
      instrumentName: string;
      instrumentContractName: string;
      blocksMetadata: FormBlockMetadataById;
      fieldsMetadata: FieldMetadataById;
      productGraph: ProductGraph;
      version: number;
      actions: readonly QuoteAction[];
      quoteQuery: unknown;
      remunerationMode: string;
      validationErrors?: readonly FormError[];
      negotiationId: string | undefined;
      etag?: string;
      sendWholeFormData?: boolean;
    },
  ) {
    return {
      type: 'FORM_RELOADED' as const,
      workspace: 'dynamic' as const,
      tabId,
      ...data,
    };
  },
};
